import { AlertProps, Box, BoxProps, Collapse, Zoom } from '@mui/material';
import React from 'react';
import { Alert } from '../Alert/alert.components';
import { L10nHTML } from '@uplift/hooks';

type ShowAlertOptions = {
  severity?: AlertProps['severity'];
  title?: React.ReactNode;
  persist?: boolean;
  preventDuplicate?: boolean;
  showIcon?: boolean;
  timeout?: number;
};

type AlertItem = {
  id: number;
  message: React.ReactNode;
  shown: boolean;

  options: ShowAlertOptions;
};


type AlertsConfig = {
  timeout?: number;
  containerPlacement?: 'center' | BoxProps['sx'];
};


const AlertsContext = React.createContext<{
  alerts: AlertItem[];
  setAlerts: React.Dispatch<React.SetStateAction<AlertItem[]>>;
  config: AlertsConfig;
} | null>(null);


export const AlertProvider: React.FC<AlertsConfig> = ({ children, timeout = 5000, containerPlacement = 'center', ...props }) => {
  const [ alerts, setAlerts ] = React.useState<AlertItem[]>([]);

  return (
    <AlertsContext.Provider
      value={{
        alerts: alerts,
        setAlerts: setAlerts,
        config: {
          ...props,
          timeout,
          containerPlacement,
        },
      }}
    >
      {children}
      <AlertsContainer />
    </AlertsContext.Provider>
  );
};

export const useAlertsContext = () => {
  const context = React.useContext(AlertsContext);

  if (!context) {
    throw new Error('useAlert must be used within an AlertsProvider');
  }

  return context;
};

export const useAlert = () => {
  const context = useAlertsContext();

  const showAlert = (message: React.ReactNode, options?: ShowAlertOptions) => {
    const id = new Date().getTime();
    const item = {
      id: new Date().getTime(),
      shown: true,
      message,
      options: {
        ...options,
        severity: options?.severity ?? 'success',
      }
    };

    if (options?.preventDuplicate) {
      const duplicate = context.alerts.find(alert => alert.message === message && alert.options.severity === options.severity);

      if (duplicate) {
        return duplicate.id;
      }
    }

    context.setAlerts(_alerts => {
      return [ item, ..._alerts ];
    });

    if (!options?.persist) {
      setTimeout(() => {
        context.setAlerts(_alerts => {
          const alerts = [ ..._alerts ];
          const alertIndex = alerts.findIndex(alert => alert.id === id);

          alerts[alertIndex] = { ...item, shown: false };
          return alerts;
        });
      }, options?.timeout ?? context.config.timeout);
    }

    return item.id;
  };

  const showAlertRef = React.useRef(showAlert);
  const contextRef = React.useRef(context);

  showAlertRef.current = showAlert;
  contextRef.current = context;

  return React.useMemo(() => {
    const showAlert = showAlertRef.current;
    const context = contextRef.current;

    return {
      show: (message: React.ReactNode, options?: ShowAlertOptions) => showAlert(message, options),
      success: (message: React.ReactNode, options?: ShowAlertOptions) => showAlert(message, {
        severity: 'success',
        ...options,
      }),
      info: (message: React.ReactNode, options?: ShowAlertOptions) => showAlert(message, {
        severity: 'info',
        ...options,
      }),
      error: (message: React.ReactNode, options?: ShowAlertOptions) => showAlert(message, {
        severity: 'error',
        ...options,
      }),
      warning: (message: React.ReactNode, options?: ShowAlertOptions) => showAlert(message, {
        severity: 'warning',
        ...options,
      }),
      remove: (id: number) => context.setAlerts(_alerts => _alerts.filter(alert => alert.id !== id)),
      removeAll: () => context.setAlerts(_alerts => _alerts.map(alert => ({ ...alert, shown: false }))),
    };
  }, []);
};


export const AlertsContainer: React.FC = () => {
  const alertsContext = useAlertsContext();

  const placement: BoxProps['sx'] = React.useMemo(() => {
    if (alertsContext.config.containerPlacement === 'center') {
      return {
        left: '50%',
        top: '16px',
        transform: 'translateX(-50%)',
      };
    }

    return alertsContext.config.containerPlacement;
  }, [ alertsContext.config.containerPlacement ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      // Put it over the max zindex of the MUI
      // https://mui.com/material-ui/customization/z-index/
      zIndex={1600}
      position="fixed"
      sx={{
        ...placement,
        pointerEvents: 'none',
        maxWidth: '60vw',
      }}
    >
      {alertsContext.alerts.map((alert) => (
        <Collapse
          style={{ maxWidth: '100%' }}
          key={alert.id}
          in={alert.shown}
          appear={true}
          timeout={300}
          easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
          onExited={() => {
            alertsContext.setAlerts(_alerts => _alerts.filter(a => a.id !== alert.id));
          }}
        >
          <Zoom
            in={alert.shown}
            timeout={300}
            easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
          >
            <Box pt={1} sx={{ pointerEvents: 'all' }}>
              <Alert
                key={alert.id}
                severity={alert.options.severity}
                title={alert.options.title}
                showIcon={alert.options?.showIcon}
                children={typeof alert.message === 'string'
                  ? <L10nHTML style={{ fontSize: 'inherit' }} children={alert.message} />
                  : alert.message}
              />
            </Box>
          </Zoom>
        </Collapse>
      ))}
    </Box>
  );
};
